import { useState, useEffect, useRef, useCallback } from "react";
import { useForm } from "react-hook-form";
import { useLocation } from "react-router-dom";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import {
  Card,
  Button,
  Input,
  Select,
  DatePicker,
  TimePicker,
  Typography,
  Space,
  message,
  Collapse,
  Tag,
  Skeleton,
} from "antd";
import type { CollapseProps } from "antd";
import {
  SaveOutlined,
  CloseOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import "dayjs/locale/pt-br";
import { ClienteSearchUnified } from "@/features/contratos/components/form/ClienteSearchUnified";
import { ClienteAutocomplete } from "@/components/ui/cliente-autocomplete";
import { useClienteAutocomplete } from "@/hooks/useClienteAutocomplete";
import { Agendamento, AgendamentoFormData, AgendamentoStatus } from "../types/agendamento";
import { agendamentosService } from "../services/agendamentos.service";
import { useAuth, UserRole } from "@/features/auth";
import { ConsultoresSupabaseService } from "@/services/supabase/consultoresService";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { formatPhone } from "@/utils/format";
import { PipelineFieldRow, PIPELINE_INPUT_CLASS, PIPELINE_DATE_SUFFIX } from "@/components/form/pipelineFieldRow";
import { PipelineFormProvider } from "@/components/form/PipelineFormContext";
import { Form } from "@/components/ui/form";
import {
  clienteFormSchema,
  ClienteFormValues,
} from "@/features/clientes/components/ClienteFormSchema";
import { QualificacaoStep } from "@/features/clientes/components/steps/QualificacaoStep";
import { AdditionalInfoStep } from "@/features/clientes/components/steps/AdditionalInfoStep";
import { ClientesService } from "@/features/clientes/services/clientes.service";
import { ClienteInfoDisplay } from "@/features/clientes/components/ClienteInfoDisplay";
import { ClienteBasicoModal } from "@/features/clientes/components/ClienteBasicoModal";
import type { Cliente } from "@/features/clientes/types/cliente";

dayjs.locale("pt-br");

type SectionStatus = "empty" | "partial" | "complete" | "error";

function clienteToFormValues(cliente: Cliente): ClienteFormValues {
  return {
    nome: cliente.nome || "",
    email: cliente.email || "",
    tel1: cliente.tel1 || "",
    tel2: cliente.tel2 || "",
    cpf: cliente.cpf || "",
    temEntrada: cliente.tem_entrada || false,
    tipoBem: (cliente.tipo_bem as ClienteFormValues["tipoBem"]) || "IMOVEL",
    valorEntrada: cliente.valor_entrada ? Number(cliente.valor_entrada) : undefined,
    valorParcelaOrcamento: cliente.valor_parcela_orcamento
      ? Number(cliente.valor_parcela_orcamento)
      : undefined,
    tempoProcura: cliente.tempo_procura as ClienteFormValues["tempoProcura"],
    prioridade: cliente.prioridade as ClienteFormValues["prioridade"],
    tempoAluguel: cliente.tempo_aluguel || "",
    statusAtual: cliente.status_atual || "",
  } as ClienteFormValues;
}

function buildClientePayload(
  data: ClienteFormValues,
  tenantId: number,
  createdBy: string
): Partial<Cliente> {
  return {
    tenant_id: tenantId,
    created_by: createdBy,
    nome: data.nome,
    email: data.email || undefined,
    tel1: data.tel1 || undefined,
    tel2: data.tel2 || undefined,
    cpf: data.cpf || undefined,
    nacionalidade: "Brasileira",
    estado_civil: "solteiro",
    tem_entrada: data.temEntrada || false,
    tipo_bem: data.tipoBem || "IMOVEL",
    valor_entrada: data.valorEntrada ? data.valorEntrada.toString() : undefined,
    valor_parcela_orcamento: data.valorParcelaOrcamento
      ? data.valorParcelaOrcamento.toString()
      : undefined,
    tempo_procura: data.tempoProcura || undefined,
    prioridade: data.prioridade || undefined,
    tempo_aluguel: data.tempoAluguel || undefined,
    cliente_qualificado: data.temEntrada === true,
    status_atual: data.statusAtual || undefined,
    status: "draft",
  };
}

function SectionStatusTag({ status }: { status: SectionStatus }) {
  if (status === "complete") return <Tag color="green" style={{ fontSize: "11px" }}>Preenchido</Tag>;
  if (status === "partial") return <Tag color="orange" style={{ fontSize: "11px" }}>Parcial</Tag>;
  if (status === "error") return <Tag color="red" style={{ fontSize: "11px" }}>Erro</Tag>;
  return <Tag style={{ fontSize: "11px" }}>Vazio</Tag>;
}

function collapsePanelLabel(title: string, subtitle: string, status: SectionStatus) {
  return (
    <div className="flex items-center justify-between w-full">
      <div className="flex items-center gap-3 min-w-0">
        <span className="text-sm font-semibold">{title}</span>
        <span className="text-xs text-gray-500 truncate">{subtitle}</span>
      </div>
      <div onClick={(e) => e.stopPropagation()} className="ml-4 flex-shrink-0">
        <SectionStatusTag status={status} />
      </div>
    </div>
  );
}

const { Title, Text } = Typography;
const { TextArea } = Input;

const agendamentoBaseSchema = z.object({
  cliente_id: z.preprocess(
    (val) => {
      if (val === undefined || val === null) return undefined;
      if (typeof val === 'string') {
        const num = parseInt(val, 10);
        return isNaN(num) ? undefined : num;
      }
      return typeof val === 'number' ? val : undefined;
    },
    z.number().min(1, "Cliente é obrigatório").optional()
  ),
  cliente_search: z.string().optional(),
  data_agendamento: z.string().min(1, "Data é obrigatória"),
  hora_agendamento: z.string().min(1, "Hora é obrigatória"),
  status: z.enum(['PENDENTE', 'CONFIRMADO', 'REALIZADO', 'CANCELADO', 'FALTOU', 'NOVA_TENTATIVA', 'REAGENDADO'], {
    required_error: "Status é obrigatório"
  }),
  observacoes: z.string().optional(),
  lider_vendas_id: z.number().optional(),
});

const agendamentoSchema = agendamentoBaseSchema.refine(
  (data) => data.cliente_id !== undefined && data.cliente_id !== null && data.cliente_id > 0,
  {
    message: "Cliente é obrigatório",
    path: ["cliente_id"],
  }
);

/** Criação de lead na pipeline — cliente é criado/atualizado no submit */
const pipelineCreateAgendamentoSchema = agendamentoBaseSchema;

interface AgendamentoFormProps {
  agendamento?: Agendamento;
  onSuccess?: () => void;
  onCancel?: () => void;
  /** Sem wrapper Card — para uso em modal genérico */
  embedded?: boolean;
  /** Layout estilo Kommo (label à esquerda, valor à direita) */
  variant?: 'default' | 'pipeline';
  hideActions?: boolean;
  responsavelNome?: string;
  onLoadingChange?: (loading: boolean) => void;
  onCreated?: (agendamento: Agendamento) => void | Promise<void>;
  preselectedClienteId?: number;
  /** Lead já vinculado — oculta campo Cliente (nome fica no cabeçalho do modal) */
  hideClienteField?: boolean;
  /** Notifica quando os campos do agendamento divergem do último estado salvo */
  onDirtyChange?: (dirty: boolean) => void;
}

const pipelineInputClass = PIPELINE_INPUT_CLASS;

export function AgendamentoForm({
  agendamento,
  onSuccess,
  onCancel,
  embedded = false,
  variant = 'default',
  hideActions = false,
  responsavelNome,
  onLoadingChange,
  onCreated,
  preselectedClienteId,
  hideClienteField = false,
  onDirtyChange,
}: AgendamentoFormProps) {
  const isPipeline = variant === 'pipeline';
  const { profile } = useAuth();
  const queryClient = useQueryClient();
  const [messageApi, contextHolder] = message.useMessage();
  const [selectedCliente, setSelectedCliente] = useState<any>(agendamento?.cliente);
  const [selectedClienteFull, setSelectedClienteFull] = useState<Cliente | null>(null);
  const [clienteModalOpen, setClienteModalOpen] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [date, setDate] = useState<dayjs.Dayjs | null>(() => {
    if (agendamento?.data_agendamento) {
      const dateOnly = agendamento.data_agendamento.slice(0, 10);
      return dayjs(dateOnly, 'YYYY-MM-DD');
    }
    return dayjs();
  });
  const [time, setTime] = useState<dayjs.Dayjs | null>(
    agendamento?.hora_agendamento ? dayjs(agendamento.hora_agendamento, 'HH:mm') : dayjs()
  );
  const [relacionadorId, setRelacionadorId] = useState<number | undefined>(agendamento?.relacionador_id);
  const [lideresDisponiveis, setLideresDisponiveis] = useState<Array<{ id: number; nome: string }>>([]);
  const [loadingLideres, setLoadingLideres] = useState(false);

  // Hook para autocompletar de clientes (usado no modo pipeline)
  const { searchTerm, setSearchTerm, clientes, isLoading: loadingClientes, selectedCliente: acSelectedCliente, setSelectedCliente: setAcSelectedCliente } = useClienteAutocomplete();

  const { state: locationState } = useLocation();
  const isReagendando = locationState?.mode === 'reagendar';

  const isCreateLead = isPipeline && !hideClienteField && !agendamento;

  const form = useForm<AgendamentoFormData>({
    resolver: zodResolver(
      isCreateLead ? pipelineCreateAgendamentoSchema : agendamentoSchema
    ),
    defaultValues: {
      cliente_id: agendamento?.cliente_id,
      data_agendamento: agendamento?.data_agendamento?.slice(0, 10) || dayjs().format('YYYY-MM-DD'),
      hora_agendamento: agendamento?.hora_agendamento || dayjs().format('HH:mm'),
      status: isReagendando ? 'REAGENDADO' : (agendamento?.status || 'PENDENTE'),
      observacoes: agendamento?.observacoes || '',
      lider_vendas_id: agendamento?.lider_vendas_id,
    },
  });
  const { setValue } = form;

  const watchedStatus = form.watch("status");
  const watchedLiderId = form.watch("lider_vendas_id");
  const watchedObservacoes = form.watch("observacoes");
  const currentSnapshot = JSON.stringify({
    data: date?.format("YYYY-MM-DD") ?? null,
    hora: time?.format("HH:mm") ?? null,
    status: watchedStatus ?? null,
    lider: watchedLiderId ?? null,
    observacoes: watchedObservacoes ?? "",
  });
  /** Último estado salvo — base para detectar alterações pendentes */
  const [savedSnapshot, setSavedSnapshot] = useState(currentSnapshot);
  const isDirty = currentSnapshot !== savedSnapshot;

  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty, onDirtyChange]);

  const clienteForm = useForm<ClienteFormValues>({
    resolver: zodResolver(clienteFormSchema),
    defaultValues: {
      nome: "",
      email: "",
      tel1: "",
      tel2: "",
      cpf: "",
      temEntrada: false,
      tipoBem: "IMOVEL",
      tempoAluguel: "",
      statusAtual: "",
    },
    mode: "onChange",
  });

  const [openPanels, setOpenPanels] = useState<string[]>(
    isCreateLead ? ["cliente"] : ["agendamento"]
  );

  /** Campos de agendamento só contam como preenchidos após interação do usuário (defaults não contam) */
  const [agendamentoTouched, setAgendamentoTouched] = useState({
    data: !!agendamento?.data_agendamento,
    hora: !!agendamento?.hora_agendamento,
    status: !!agendamento?.status,
  });

  const markAgendamentoFieldTouched = (field: "data" | "hora" | "status") => {
    setAgendamentoTouched((prev) => ({ ...prev, [field]: true }));
  };

  const clienteSectionFields = ["nome", "tel1", "email", "cpf"] as const;
  const qualificacaoSectionFields = [
    "tipoBem",
    "valorParcelaOrcamento",
    "tempoProcura",
    "prioridade",
  ] as const;

  const getClienteSectionStatus = (
    sectionKey: "cliente" | "qualificacao" | "observacoes"
  ): SectionStatus => {
    const errors = clienteForm.formState.errors;
    const values = clienteForm.getValues();
    const trackedFields =
      sectionKey === "cliente"
        ? clienteSectionFields
        : sectionKey === "qualificacao"
          ? qualificacaoSectionFields
          : (["statusAtual"] as const);

    if (trackedFields.some((field) => field in errors)) return "error";

    const completedFields = trackedFields.filter((field) => {
      const val = values[field as keyof ClienteFormValues];
      if (typeof val === "boolean") return val === true;
      const hasValue = val !== undefined && val !== null && val !== "";
      if (!hasValue) return false;
      if (sectionKey === "qualificacao" && isCreateLead) {
        return !!clienteForm.formState.dirtyFields[field as keyof ClienteFormValues];
      }
      return true;
    });

    if (completedFields.length === 0) return "empty";
    if (completedFields.length === trackedFields.length) return "complete";
    return "partial";
  };

  const getAgendamentoSectionStatus = (): SectionStatus => {
    const errors = form.formState.errors;
    const tracked = [
      { field: "data_agendamento" as const, touched: agendamentoTouched.data },
      { field: "hora_agendamento" as const, touched: agendamentoTouched.hora },
      { field: "status" as const, touched: agendamentoTouched.status },
    ];

    if (tracked.some(({ field }) => field in errors)) return "error";

    const userFilled = tracked.filter(({ touched }) => touched);

    if (userFilled.length === 0) return "empty";
    if (userFilled.length === tracked.length) return "complete";
    return "partial";
  };
  const preselectedLoadedRef = useRef<number | null>(null);
  const agendamentoSyncedRef = useRef<number | null>(null);

  // Carregar cliente pré-selecionado
  useEffect(() => {
    if (!preselectedClienteId) {
      preselectedLoadedRef.current = null;
      return;
    }
    if (preselectedLoadedRef.current === preselectedClienteId) return;

    const carregarClientePreselecionado = async () => {
      try {
        const { data } = await supabase
          .from('clientes')
          .select('id, nome, cpf, email, tel1, tel2')
          .eq('id', preselectedClienteId)
          .single();

        if (data) {
          preselectedLoadedRef.current = preselectedClienteId;
          setSelectedCliente(data);
          setAcSelectedCliente(data);
          const clienteId = typeof data.id === 'string' ? parseInt(data.id, 10) : Number(data.id);
          setValue('cliente_id', clienteId);
          if (!hideClienteField) {
            setSearchTerm(data.nome);
          }
        }
      } catch (error) {
        console.error('Erro ao carregar cliente pré-selecionado:', error);
      }
    };

    void carregarClientePreselecionado();
  }, [preselectedClienteId, hideClienteField, setValue, setAcSelectedCliente, setSearchTerm]);

  // Sincronizar cliente quando agendamento existente é carregado
  useEffect(() => {
    const agendamentoId = agendamento?.id;
    if (!agendamentoId) {
      agendamentoSyncedRef.current = null;
      return;
    }
    if (agendamentoSyncedRef.current === agendamentoId) return;

    if (!agendamento.cliente_id && !agendamento.cliente) return;

    agendamentoSyncedRef.current = agendamentoId;

    const cliente = agendamento.cliente;
    if (cliente) {
      const clienteOption = {
        id: cliente.id,
        nome: cliente.nome,
        cpf: null,
        email: cliente.email ?? null,
        tel1: cliente.tel1 ?? null,
      };
      setSelectedCliente(clienteOption);
      setAcSelectedCliente(clienteOption);
    }

    const id =
      agendamento.cliente_id ??
      (cliente?.id != null
        ? typeof cliente.id === 'string'
          ? parseInt(cliente.id, 10)
          : Number(cliente.id)
        : undefined);

    if (id) {
      setValue('cliente_id', id);
    }
  }, [agendamento?.id, agendamento?.cliente_id, agendamento?.cliente, setValue, setAcSelectedCliente]);

  useEffect(() => {
    if (agendamento?.id) {
      setAgendamentoTouched({ data: true, hora: true, status: true });
    }
  }, [agendamento?.id]);

  /** Telefone é obrigatório para o lead — sinaliza já na seleção, não só no submit */
  const flagTelefoneAusente = useCallback((cliente: Cliente) => {
    if (!cliente.tel1?.trim()) {
      clienteForm.setError("tel1", {
        type: "manual",
        message: "Telefone é obrigatório. Clique em \"Editar dados\" para cadastrar.",
      });
    }
  }, [clienteForm]);

  /** Último cliente carregado no clienteForm — evita recarregar (e sobrescrever a qualificação digitada) */
  const clienteLoadedIdRef = useRef<number | null>(null);

  const loadClienteIntoForm = useCallback(async (clienteId: number) => {
    clienteLoadedIdRef.current = clienteId;
    try {
      const full = await ClientesService.buscarCliente(String(clienteId));
      clienteForm.reset(clienteToFormValues(full));
      setSelectedClienteFull(full);
      flagTelefoneAusente(full);
    } catch (error) {
      console.error("Erro ao carregar dados do cliente:", error);
    }
  }, [clienteForm, flagTelefoneAusente]);

  const clearSelectedCliente = () => {
    clienteLoadedIdRef.current = null;
    setAcSelectedCliente(null);
    setSelectedCliente(null);
    setSelectedClienteFull(null);
    form.setValue("cliente_id", undefined);
    setSearchTerm("");
    clienteForm.reset({
      nome: "",
      email: "",
      tel1: "",
      tel2: "",
      cpf: "",
      temEntrada: false,
      tipoBem: "IMOVEL",
      tempoAluguel: "",
      statusAtual: "",
    });
  };

  useEffect(() => {
    if (!isCreateLead || !selectedCliente?.id) return;
    const id =
      typeof selectedCliente.id === "string"
        ? parseInt(selectedCliente.id, 10)
        : Number(selectedCliente.id);
    if (!Number.isNaN(id) && clienteLoadedIdRef.current !== id) {
      void loadClienteIntoForm(id);
    }
  }, [isCreateLead, selectedCliente?.id, loadClienteIntoForm]);

  // Buscar relacionador_id e líderes do mesmo time
  useEffect(() => {
    const buscarDados = async () => {
      if (!profile?.uuid) return;

      try {
        const { data: consultorAtual } = await supabase
          .from('consultores')
          .select('id, tipo, supervisor_id')
          .eq('user_id', profile.uuid)
          .single();

        if (!consultorAtual) {
          // SuperAdmin não passa pelos fluxos que criam consultor automaticamente ao ser
          // aprovado/convidado (ver ConsultoresSupabaseService); criamos aqui sob demanda para
          // que ele possa se auto-associar como relacionador e concluir o processo sozinho.
          if (profile.role === UserRole.SUPER_ADMIN && profile.tenant_id) {
            const { data: profileRow } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', profile.uuid)
              .single();

            if (profileRow) {
              const resultado = await ConsultoresSupabaseService.criarConsultorDeProfile(profileRow as any);
              if (resultado.success && resultado.consultorId && !agendamento) {
                setRelacionadorId(resultado.consultorId);
              }
            }
          }
          return;
        }

        // Sempre definir o relacionadorId como o consultor atual se estivermos criando um novo
        if (!agendamento) {
          setRelacionadorId(consultorAtual.id);
        }

        // Fluxo de busca de quem pode receber o agendamento
        if (consultorAtual.supervisor_id) {
          setLoadingLideres(true);
          // Se for relacionador, buscar líderes. Se for líder, buscar outros líderes do mesmo supervisor
          const { data: lideres, error: lideresError } = await supabase
            .from('consultores')
            .select('id, nome')
            .eq('supervisor_id', consultorAtual.supervisor_id)
            .eq('tipo', 'lider')
            .eq('status', 'ativo')
            .order('nome');

          if (lideres && !lideresError) {
            setLideresDisponiveis(lideres);
          }
          setLoadingLideres(false);
        }
      } catch (error) {
        console.error('Erro ao buscar dados:', error);
        setLoadingLideres(false);
      }
    };

    if (profile?.uuid) {
      buscarDados();
    }
  }, [profile?.uuid]);

  // Com um único líder disponível, mantém o campo visível e pré-seleciona
  useEffect(() => {
    if (agendamento) return;
    if (lideresDisponiveis.length !== 1) return;
    if (form.getValues('lider_vendas_id')) return;
    const liderId = lideresDisponiveis[0].id;
    form.setValue('lider_vendas_id', liderId);
    // Pré-seleção automática não conta como alteração do usuário
    setSavedSnapshot((prev) => JSON.stringify({ ...JSON.parse(prev), lider: liderId }));
  }, [lideresDisponiveis, agendamento, form]);

  const onSubmit = async (data: AgendamentoFormData) => {
    let clienteId = selectedCliente?.id
      ? typeof selectedCliente.id === "string"
        ? parseInt(selectedCliente.id, 10)
        : Number(selectedCliente.id)
      : data.cliente_id
        ? typeof data.cliente_id === "string"
          ? parseInt(data.cliente_id, 10)
          : Number(data.cliente_id)
        : undefined;

    if (isCreateLead) {
      const clienteValid = await clienteForm.trigger();
      if (!clienteValid) {
        const errors = clienteForm.formState.errors;
        const firstErrorField = Object.keys(errors)[0];
        const panel =
          firstErrorField && clienteSectionFields.includes(firstErrorField as typeof clienteSectionFields[number])
            ? "cliente"
            : firstErrorField && qualificacaoSectionFields.includes(firstErrorField as typeof qualificacaoSectionFields[number])
              ? "qualificacao"
              : firstErrorField === "statusAtual"
                ? "observacoes"
                : "cliente";
        setOpenPanels([panel]);
        messageApi.error("Preencha os dados do cliente e qualificação");
        return;
      }

      const clienteValues = clienteForm.getValues();
      const nomeLead = clienteValues.nome?.trim() || searchTerm.trim();

      if (!nomeLead) {
        clienteForm.setError("nome", { message: "Nome é obrigatório" });
        setOpenPanels(["cliente"]);
        messageApi.error("Informe o nome do lead");
        return;
      }

      if (!clienteValues.tel1?.trim()) {
        clienteForm.setError("tel1", { message: "Telefone é obrigatório" });
        setOpenPanels(["cliente"]);
        messageApi.error("Informe o telefone do lead");
        return;
      }

      try {
        const tenantId = Number(profile?.tenant_id);
        const createdBy = profile?.uuid;
        if (!tenantId || !createdBy) {
          messageApi.error("Usuário ou tenant não identificado");
          return;
        }

        const payload = buildClientePayload(
          { ...clienteValues, nome: nomeLead },
          tenantId,
          createdBy
        );

        if (clienteId) {
          // Não reenviar `status` em atualizações — evita derrubar um cliente já ativo de volta para rascunho
          const { status: _status, ...updatePayload } = payload;
          await ClientesService.atualizarCliente(String(clienteId), updatePayload);
        } else {
          const criado = await ClientesService.criarCliente(payload);
          clienteId =
            typeof criado.id === "string" ? parseInt(criado.id, 10) : Number(criado.id);
          setSelectedCliente({
            id: clienteId,
            nome: criado.nome,
            cpf: criado.cpf ?? null,
            email: criado.email ?? null,
            tel1: criado.tel1 ?? null,
          });
          setAcSelectedCliente({
            id: clienteId,
            nome: criado.nome,
            cpf: criado.cpf ?? null,
            email: criado.email ?? null,
            tel1: criado.tel1 ?? null,
          });
          setValue("cliente_id", clienteId);
        }

        await queryClient.invalidateQueries({ queryKey: ["clientes"] });
      } catch (error: unknown) {
        console.error("Erro ao salvar cliente do lead:", error);
        messageApi.error(
          error instanceof Error ? error.message : "Erro ao salvar dados do cliente"
        );
        return;
      }
    } else if (!selectedCliente && !clienteId) {
      form.setError("cliente_id", {
        type: "manual",
        message: "Selecione um cliente",
      });
      messageApi.error("Selecione um cliente");
      return;
    }

    // Validação da data
    if (!date && !data.data_agendamento) {
      form.setError('data_agendamento', {
        type: 'manual',
        message: 'Selecione uma data',
      });
      setOpenPanels(['agendamento']);
      messageApi.error("Selecione uma data");
      return;
    }

    // Validação do relacionador
    if (!relacionadorId) {
      messageApi.error("Relacionador não encontrado");
      return;
    }


    // Validação de Conflito
    if (data.lider_vendas_id) {
      try {
        const temConflito = await agendamentosService.verificarConflito(
          date ? date.format('YYYY-MM-DD') : data.data_agendamento,
          time ? time.format('HH:mm') : data.hora_agendamento,
          data.lider_vendas_id,
          agendamento?.id
        );

        if (temConflito) {
          messageApi.error("Já existe um agendamento para este Líder de Vendas neste horário!");
          return;
        }
      } catch (error: any) {
        console.error('Erro ao verificar conflito de agendamento:', error);
        messageApi.error(error?.message || "Erro ao verificar conflito de horário");
        return;
      }
    }

    setIsSubmitting(true);
    onLoadingChange?.(true);

    try {
      const snapshotAoSalvar = currentSnapshot;
      const agendamentoData = {
        tenant_id: Number(profile?.tenant_id),
        cliente_id: clienteId,
        relacionador_id: relacionadorId,
        lider_vendas_id: data.lider_vendas_id || undefined,
        data_agendamento: date ? date.format('YYYY-MM-DD') : data.data_agendamento,
        hora_agendamento: time ? time.format('HH:mm') : data.hora_agendamento,
        status: data.status as AgendamentoStatus,
        observacoes: data.observacoes,
        created_by: profile?.uuid,
      };

      if (agendamento) {
        // Em edição, preserva tenant e autoria originais
        const { tenant_id: _tenantId, created_by: _createdBy, ...updateData } = agendamentoData;
        const atualizado = await agendamentosService.atualizarAgendamento(agendamento.id, updateData);
        messageApi.success("Agendamento atualizado com sucesso!");
        await onCreated?.(atualizado);
      } else {
        const criado = await agendamentosService.criarAgendamento(agendamentoData);
        if (!embedded && !isPipeline) {
          messageApi.success("Agendamento criado com sucesso!");
        }
        await onCreated?.(criado);
      }

      setSavedSnapshot(snapshotAoSalvar);
      await queryClient.invalidateQueries({ queryKey: ['agendamentos'] });
      await queryClient.invalidateQueries({ queryKey: ['agendamentos-stats'] });
      onSuccess?.();
    } catch (error: any) {
      console.error('Erro ao salvar agendamento:', error);
      messageApi.error(error?.message || "Erro ao salvar agendamento");
    } finally {
      setIsSubmitting(false);
      onLoadingChange?.(false);
    }
  };

  const statusOptions = [
    { label: "Pendente", value: "PENDENTE" },
    { label: "Confirmado", value: "CONFIRMADO" },
    { label: "Realizado", value: "REALIZADO" },
    { label: "Cancelado", value: "CANCELADO" },
    { label: "Faltou", value: "FALTOU" },
    { label: "Nova Tentativa", value: "NOVA_TENTATIVA" },
    { label: "Reagendado", value: "REAGENDADO" },
  ];

  const agendamentoFields = (
    <>
      <PipelineFieldRow label="Usuário responsável">
        <span className="text-[13px] text-gray-800 dark:text-gray-200 leading-8">
          {responsavelNome || "—"}
        </span>
      </PipelineFieldRow>

      <PipelineFieldRow
        label="Data do agendamento"
        required
        error={form.formState.errors.data_agendamento?.message}
      >
        <DatePicker
          value={date}
          onChange={(d) => {
            setDate(d);
            markAgendamentoFieldTouched("data");
            if (d) {
              form.setValue("data_agendamento", d.format("YYYY-MM-DD"));
              form.clearErrors("data_agendamento");
            }
          }}
          format="DD/MM/YYYY"
          placeholder="Selecione"
          variant="borderless"
          className={pipelineInputClass}
          suffixIcon={PIPELINE_DATE_SUFFIX}
          disabledDate={(current) => current && current < dayjs().startOf("day")}
          style={{ width: "100%" }}
          status={form.formState.errors.data_agendamento ? "error" : undefined}
        />
      </PipelineFieldRow>

      <PipelineFieldRow
        label="Hora do agendamento"
        required
        error={form.formState.errors.hora_agendamento?.message}
      >
        <TimePicker
          value={time}
          onChange={(t) => {
            setTime(t);
            markAgendamentoFieldTouched("hora");
            if (t) {
              form.setValue("hora_agendamento", t.format("HH:mm"));
              form.clearErrors("hora_agendamento");
            }
          }}
          format="HH:mm"
          placeholder="Selecione"
          variant="borderless"
          className={pipelineInputClass}
          minuteStep={15}
          disabledTime={() => {
            const isToday = date && date.isSame(dayjs(), "day");
            if (!isToday) return {};
            const now = dayjs();
            const currentHour = now.hour();
            const currentMinute = now.minute();
            return {
              disabledHours: () =>
                Array.from({ length: 24 }, (_, i) => i).filter((h) => h < currentHour),
              disabledMinutes: (selectedHour: number) => {
                if (selectedHour === currentHour) {
                  return Array.from({ length: 60 }, (_, i) => i).filter((m) => m < currentMinute);
                }
                return [];
              },
            };
          }}
          style={{ width: "100%" }}
          status={form.formState.errors.hora_agendamento ? "error" : undefined}
        />
      </PipelineFieldRow>

      <PipelineFieldRow
        label="Status"
        required
        error={form.formState.errors.status?.message}
      >
        <Select
          value={form.watch("status")}
          onChange={(value) => {
            markAgendamentoFieldTouched("status");
            form.setValue("status", value as AgendamentoStatus);
            form.clearErrors("status");
          }}
          placeholder="Selecione"
          variant="borderless"
          className={pipelineInputClass}
          style={{ width: "100%" }}
          status={form.formState.errors.status ? "error" : undefined}
          options={statusOptions}
        />
      </PipelineFieldRow>

      {lideresDisponiveis.length > 0 && (
        <PipelineFieldRow label="Líder de vendas">
          <Select
            value={form.watch("lider_vendas_id")?.toString() || "none"}
            onChange={(value) => {
              if (value === "none") {
                form.setValue("lider_vendas_id", undefined);
              } else {
                form.setValue("lider_vendas_id", parseInt(value, 10));
              }
              form.clearErrors("lider_vendas_id");
            }}
            disabled={loadingLideres}
            placeholder="Selecione"
            variant="borderless"
            className={pipelineInputClass}
            style={{ width: "100%" }}
            options={[
              { label: "Nenhum", value: "none" },
              ...lideresDisponiveis.map((lider) => ({
                label: lider.nome,
                value: lider.id.toString(),
              })),
            ]}
          />
        </PipelineFieldRow>
      )}

      <PipelineFieldRow label="Observações">
        <TextArea
          {...form.register("observacoes")}
          value={form.watch("observacoes")}
          onChange={(e) => form.setValue("observacoes", e.target.value)}
          placeholder="..."
          rows={3}
          variant="borderless"
          className="!px-1 !text-[13px] resize-none"
        />
      </PipelineFieldRow>
    </>
  );

  const pipelineCollapseItems: CollapseProps["items"] = (() => {
    const items: NonNullable<CollapseProps["items"]> = [];

    if (isCreateLead) {
      items.push(
        {
          key: "cliente",
          label: collapsePanelLabel(
            "Cliente",
            "Nome, telefone e busca de existente",
            getClienteSectionStatus("cliente")
          ),
          children: (
            <div className="pt-2 space-y-3">
              {acSelectedCliente ? (
                selectedClienteFull ? (
                  <ClienteInfoDisplay
                    cliente={selectedClienteFull}
                    compact
                    onEdit={() => setClienteModalOpen(true)}
                    onRemove={clearSelectedCliente}
                    telefoneError={clienteForm.formState.errors.tel1?.message}
                  />
                ) : (
                  <Skeleton active paragraph={{ rows: 3 }} />
                )
              ) : (
                <>
                  <PipelineFieldRow
                    label="Buscar existente"
                    error={form.formState.errors.cliente_id?.message}
                  >
                    <ClienteAutocomplete
                      value={searchTerm}
                      onChange={setSearchTerm}
                      onClienteSelect={(cliente) => {
                        setAcSelectedCliente(cliente);
                        setSelectedCliente(cliente);
                        const id =
                          typeof cliente.id === "string"
                            ? parseInt(cliente.id, 10)
                            : Number(cliente.id);
                        form.setValue("cliente_id", id);
                        setSearchTerm(cliente.nome);
                        form.clearErrors("cliente_id");
                        void loadClienteIntoForm(id);
                      }}
                      selectedCliente={acSelectedCliente}
                      onClearSelection={clearSelectedCliente}
                      clientes={clientes}
                      isLoading={loadingClientes}
                      placeholder="Nome, CPF ou tel..."
                      className="w-full"
                    />
                  </PipelineFieldRow>
                  <Button
                    type="dashed"
                    block
                    onClick={() => setClienteModalOpen(true)}
                  >
                    Criar novo cliente
                  </Button>
                </>
              )}
            </div>
          ),
        },
        {
          key: "qualificacao",
          label: collapsePanelLabel(
            "Qualificação",
            "Tipo do bem, entrada e parcela",
            getClienteSectionStatus("qualificacao")
          ),
          children: (
            <div className="pt-2">
              <QualificacaoStep form={clienteForm} />
            </div>
          ),
        },
        {
          key: "observacoes",
          label: collapsePanelLabel(
            "Observações",
            "Histórico e status atual",
            getClienteSectionStatus("observacoes")
          ),
          children: (
            <div className="pt-2">
              <AdditionalInfoStep form={clienteForm} />
            </div>
          ),
        }
      );
    }

    items.push({
      key: "agendamento",
      label: collapsePanelLabel(
        "Agendamento",
        "Data, hora e status",
        getAgendamentoSectionStatus()
      ),
      children: <div className="pt-2">{agendamentoFields}</div>,
    });

    return items;
  })();

  const formContent = isPipeline ? (
    <PipelineFormProvider variant="pipeline">
      <Form {...clienteForm}>
        <form
          id="pipeline-lead-form"
          onSubmit={form.handleSubmit(onSubmit, (errors) => {
            const errorMessages = Object.entries(errors).map(([field, error]) => {
              return `${field}: ${error?.message || "Erro de validação"}`;
            });
            setOpenPanels(["agendamento"]);
            messageApi.error(`Erro de validação: ${errorMessages.join(", ")}`);
          })}
        >
          <Collapse
            accordion
            activeKey={openPanels}
            onChange={(keys) => setOpenPanels(keys as string[])}
            className="rounded-[16px] overflow-hidden border border-[rgba(15,118,110,0.08)]"
            items={pipelineCollapseItems}
          />
        </form>
      </Form>
    </PipelineFormProvider>
  ) : (
        <form
          onSubmit={form.handleSubmit(onSubmit, (errors) => {
            console.log('❌ Erros de validação:', errors);
            const errorMessages = Object.entries(errors).map(([field, error]) => {
              return `${field}: ${error?.message || 'Erro de validação'}`;
            });
            messageApi.error(`Erro de validação: ${errorMessages.join(', ')}`);
          })}
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            {/* Busca de Cliente */}
            <div>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                Cliente <Text type="danger">*</Text>
              </Text>
              <ClienteSearchUnified
                onClienteSelect={(cliente) => {
                  setSelectedCliente(cliente);
                  const clienteId = typeof cliente.id === 'string' ? parseInt(cliente.id, 10) : Number(cliente.id);
                  form.setValue('cliente_id', clienteId);
                  form.clearErrors('cliente_id');
                }}
                selectedCliente={selectedCliente}
                placeholder="Busque por nome, telefone ou CPF"
              />
              {form.formState.errors.cliente_id && (
                <Text type="danger" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
                  {form.formState.errors.cliente_id.message}
                </Text>
              )}
              {selectedCliente && (
                <Text type="secondary" style={{ fontSize: 13, marginTop: 8, display: "block" }}>
                  Cliente selecionado: <Text strong>{selectedCliente.nome}</Text>
                  {selectedCliente.tel1 && ` - ${formatPhone(selectedCliente.tel1)}`}
                </Text>
              )}
            </div>

            {/* Data, Hora e Status */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(200px, 1fr))", gap: 16 }}>
              {/* Data do Agendamento */}
              <div>
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  Data do Agendamento <Text type="danger">*</Text>
                </Text>
                <DatePicker
                  value={date}
                  onChange={(d) => {
                    setDate(d);
                    if (d) {
                      form.setValue('data_agendamento', d.format('YYYY-MM-DD'));
                      form.clearErrors('data_agendamento');
                    }
                  }}
                  format="DD/MM/YYYY"
                  placeholder="Selecione a data"
                  disabledDate={(current) => current && current < dayjs().startOf('day')}
                  style={{ width: "100%" }}
                  status={form.formState.errors.data_agendamento ? "error" : undefined}
                />
                {form.formState.errors.data_agendamento && (
                  <Text type="danger" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
                    {form.formState.errors.data_agendamento.message}
                  </Text>
                )}
              </div>

              {/* Hora do Agendamento */}
              <div>
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  Hora do Agendamento <Text type="danger">*</Text>
                </Text>
                <TimePicker
                  value={time}
                  onChange={(t) => {
                    setTime(t);
                    if (t) {
                      form.setValue('hora_agendamento', t.format('HH:mm'));
                      form.clearErrors('hora_agendamento');
                    }
                  }}
                  format="HH:mm"
                  placeholder="Selecione a hora"
                  minuteStep={15}
                  disabledTime={() => {
                    const isToday = date && date.isSame(dayjs(), 'day');
                    if (!isToday) return {};

                    const now = dayjs();
                    const currentHour = now.hour();
                    const currentMinute = now.minute();

                    return {
                      disabledHours: () => Array.from({ length: 24 }, (_, i) => i).filter(h => h < currentHour),
                      disabledMinutes: (selectedHour: number) => {
                        if (selectedHour === currentHour) {
                          return Array.from({ length: 60 }, (_, i) => i).filter(m => m < currentMinute);
                        }
                        return [];
                      },
                    };
                  }}
                  style={{ width: "100%" }}
                  status={form.formState.errors.hora_agendamento ? "error" : undefined}
                />
                {form.formState.errors.hora_agendamento && (
                  <Text type="danger" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
                    {form.formState.errors.hora_agendamento.message}
                  </Text>
                )}
              </div>

              {/* Status */}
              <div>
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  Status <Text type="danger">*</Text>
                </Text>
                <Select
                  value={form.watch('status')}
                  onChange={(value) => {
                    form.setValue('status', value as AgendamentoStatus);
                    form.clearErrors('status');
                  }}
                  placeholder="Selecione o status"
                  style={{ width: "100%" }}
                  status={form.formState.errors.status ? "error" : undefined}
                  options={statusOptions}
                />
                {form.formState.errors.status && (
                  <Text type="danger" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
                    {form.formState.errors.status.message}
                  </Text>
                )}
              </div>
            </div>

            {/* Líder de Vendas (opcional) */}
            {lideresDisponiveis.length > 0 && (
              <div>
                <Text strong style={{ display: "block", marginBottom: 8 }}>
                  Atribuir a Líder de Vendas (opcional)
                </Text>
                <Select
                  value={form.watch('lider_vendas_id')?.toString() || "none"}
                  onChange={(value) => {
                    if (value === "none") {
                      form.setValue('lider_vendas_id', undefined);
                    } else {
                      form.setValue('lider_vendas_id', parseInt(value, 10));
                    }
                    form.clearErrors('lider_vendas_id');
                  }}
                  disabled={loadingLideres}
                  style={{ width: "100%" }}
                  options={[
                    { label: "Nenhum", value: "none" },
                    ...lideresDisponiveis.map((lider) => ({
                      label: lider.nome,
                      value: lider.id.toString(),
                    })),
                  ]}
                />
                <Text type="secondary" style={{ fontSize: 12, marginTop: 4, display: "block" }}>
                  Atribua este agendamento a um líder de vendas específico do seu time
                </Text>
              </div>
            )}

            {/* Observações */}
            <div>
              <Text strong style={{ display: "block", marginBottom: 8 }}>
                Observações
              </Text>
              <TextArea
                {...form.register('observacoes')}
                value={form.watch('observacoes')}
                onChange={(e) => form.setValue('observacoes', e.target.value)}
                placeholder="Adicione observações sobre o agendamento..."
                rows={4}
              />
            </div>

            {/* Botões */}
            {!hideActions && (
              <Space style={{ justifyContent: "flex-end", display: "flex" }}>
                {onCancel && (
                  <Button icon={<CloseOutlined />} onClick={onCancel}>
                    Cancelar
                  </Button>
                )}
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={isSubmitting}
                  icon={<SaveOutlined />}
                >
                  {agendamento ? "Atualizar" : "Criar Agendamento"}
                </Button>
              </Space>
            )}
          </div>
        </form>
  );

  return (
    <>
      {contextHolder}
      {isCreateLead && (
        <ClienteBasicoModal
          open={clienteModalOpen}
          cliente={selectedClienteFull ?? undefined}
          onClose={() => setClienteModalOpen(false)}
          onSuccess={(saved) => {
            const id = typeof saved.id === "string" ? parseInt(saved.id, 10) : Number(saved.id);
            const option = {
              id,
              nome: saved.nome,
              cpf: saved.cpf ?? null,
              email: saved.email ?? null,
              tel1: saved.tel1 ?? null,
            };
            // Só os campos de identificação — preserva Qualificação/Observações já digitadas no lead
            clienteLoadedIdRef.current = id;
            const identificacao = ["nome", "tel1", "tel2", "cpf", "email"] as const;
            identificacao.forEach((field) => clienteForm.setValue(field, saved[field] || ""));
            clienteForm.clearErrors([...identificacao]);
            setSelectedCliente(option);
            setAcSelectedCliente(option);
            setSelectedClienteFull(saved);
            setValue("cliente_id", id);
            form.clearErrors("cliente_id");
            setClienteModalOpen(false);
          }}
        />
      )}
      {embedded || isPipeline ? (
        formContent
      ) : (
        <Card
          variant="borderless"
          style={{ borderRadius: 16 }}
          title={
            <Title level={4} style={{ margin: 0 }}>
              {agendamento ? "Editar Agendamento" : "Novo Agendamento"}
            </Title>
          }
        >
          {formContent}
        </Card>
      )}
    </>
  );
}
